import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import config from "../config";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [navHeight, setNavHeight] = useState(0);
  const hoverTimeoutRef = useRef(null);
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (navRef.current) {
      setNavHeight(navRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleNavClick = (e, item) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);

    if (item.route) {
      navigate(item.route);
      return;
    }

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(item.id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return;
    }

    const el = document.getElementById(item.id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleMouseEnter = (name) => {
    clearTimeout(hoverTimeoutRef.current);
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const navItems = [
    { name: "Home", id: "home" },
    { name: "Our Services", route: "/services" },
    { name: "About Us", route: "/about-us" },
    { name: "Our Experts", route: "/team" },
  ];
  
  const resourceItems = [
    { name: "Our Blogs", route: "/blogs" },
    { name: "Case Studies", route: "/case-studies" },
  ];
  
  const ecosystemItems = [
    { name: "CTO Bridge", route: "https://ctobridge.com/" },
    { name: "CHRO Bridge", route: "https://kfwcorp.com/" },
  ];

  const locationItems = [
    { name: "UAE", route: "/", flagCode: "ae" },
    { name: "India", route: "https://cfobridge.com/", flagCode: "in" },
    { name: "USA", route: "https://cfobridge.com/usa/", flagCode: "us" },
  ];

  return (
    <>
      <style>{`
        /*
         * STRATEGY:
         * - At top: .nav-wrapper has NO overrides → your existing CSS
         *   controls .inner-nav fully (blue bg, white text, etc.)
         * - Scrolled: .nav-wrapper.is-scrolled → .main-nav goes fixed,
         *   placeholder holds the gap, and .floating-inner overrides
         *   ONLY the visual properties needed for the white pill.
         */

        /* Match dropdown triggers to other nav links */
.nav-dropdown-trigger {
  font-size: inherit;
  font-weight: inherit;
  font-family: inherit;
  letter-spacing: inherit;
  line-height: inherit;
}

        /* ── Wrapper: always in document flow ─────────────────── */
  .nav-wrapper {
    position: relative;
    width: 100%;
    z-index: 1000;
  }

  /* Placeholder: shown only when nav is fixed, holds the gap */
  .nav-placeholder {
    display: none;
  }
  
  /* Default (top) → keep white logo */
.nav-wrapper .logo img {
  filter: brightness(0) invert(1);
  transition: filter 0.3s ease, transform 0.3s ease;
}

/* Scrolled → show original colored logo */
.nav-wrapper.is-scrolled .logo img {
  filter: none;
}

  /* Default (TOP - attached) */
  .nav-wrapper .inner-nav {
    background: #014293;
    transition: background 0.35s ease;
  }

  /* ✅ FIX: fill full width to remove white gaps */
  .nav-wrapper .main-nav {
    background: #014293;
    transition: background 0.35s ease;
  }

  .nav-wrapper.is-scrolled .nav-placeholder {
    background: rgba(255, 255, 255, 0) !important;
    display: block;
  }

  /* ── Nav: relative at top, fixed when scrolled ─────────── */
  .main-nav {
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 1000;
  }

  .nav-wrapper.is-scrolled .main-nav {
    position: fixed;

    /* ✅ FIX: remove blue when floating */
    background: transparent;
  }

  /* ── Scrolled: pill overrides ──────────────────────────── */
  .nav-wrapper.is-scrolled .inner-nav {
    margin: 10px 20px !important;
    border-radius: 18px !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) saturate(1.8) !important;
    -webkit-backdrop-filter: blur(20px) saturate(1.8) !important;
    border: 1px solid rgba(0, 0, 0, 0.09) !important;
    box-shadow:
      0 1px 0 rgba(255,255,255,1) inset,
      0 8px 32px rgba(0, 0, 0, 0.12),
      0 2px 8px rgba(0, 0, 0, 0.07) !important;

    transition:
      margin        0.42s cubic-bezier(0.4, 0, 0.2, 1),
      border-radius 0.42s cubic-bezier(0.4, 0, 0.2, 1),
      box-shadow    0.42s cubic-bezier(0.4, 0, 0.2, 1),
      background    0.42s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  /* Force ALL nav text/icons dark inside the white pill */
  .nav-wrapper.is-scrolled .inner-nav *,
  .nav-wrapper.is-scrolled .inner-nav a,
  .nav-wrapper.is-scrolled .inner-nav .nav-list a,
  .nav-wrapper.is-scrolled .inner-nav .nav-dropdown-trigger {
    color: #1a2b5e !important;
  }

  /* Force button text white when scrolled */
.nav-wrapper.is-scrolled .btn-contact,
.nav-wrapper.is-scrolled .btn-contact .btn-text {
  color: #ffffff !important;
}

/* Ensure button background stays correct */
.nav-wrapper.is-scrolled .btn-contact {
  background: #014293 !important;
}
        /* ── Dropdown Wrapper ──────────────────────────────────── */
        .nav-dropdown-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
        }

        .nav-dropdown-trigger {
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  background: none;
  border: none;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  letter-spacing: normal;
  line-height: normal;
}

/* All nav links */
.nav-list a,
.nav-dropdown-trigger {
  font-weight: 600;
  position: relative;
  transition: color 0.25s ease;
}

/* Underline hover animation */
.nav-list a::after,
.nav-dropdown-trigger::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -4px;
  width: 0%;
  height: 2px;
  background: currentColor;
  transition: width 0.25s ease;
}

/* On hover */
.nav-list a:hover::after,
.nav-dropdown-trigger:hover::after {
  width: 100%;
}

/* Slight color lift on hover */
.nav-list a:hover,
.nav-dropdown-trigger:hover {
  opacity: 0.8;
}

        .dd-chevron {
          width: 9px;
          height: 9px;
          flex-shrink: 0;
          transition: transform 0.22s ease;
          opacity: 0.6;
        }

        .nav-dropdown-wrapper.open .dd-chevron {
          transform: rotate(180deg);
          opacity: 1;
        }

        /* ── Dropdown Panel ────────────────────────────────────── */
        .dd-panel {
          position: absolute;
          top: calc(100% + 18px);
          left: 50%;
          transform: translateX(-50%) translateY(-6px);
          min-width: 195px;
          padding: 6px;
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.09);
          box-shadow:
            0 4px 6px -2px rgba(0,0,0,0.06),
            0 16px 48px rgba(0, 0, 0, 0.14);
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
          z-index: 9999;
        }

        .dd-panel::before {
          content: '';
          position: absolute;
          top: -6px;
          left: 50%;
          transform: translateX(-50%) rotate(45deg);
          width: 11px;
          height: 11px;
          background: #ffffff;
          border-top: 1px solid rgba(0,0,0,0.09);
          border-left: 1px solid rgba(0,0,0,0.09);
          border-radius: 2px 0 0 0;
        }

        .nav-dropdown-wrapper.open .dd-panel {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transform: translateX(-50%) translateY(0);
        }

        /* ── Dropdown Items ────────────────────────────────────── */
        .dd-item {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 9px 12px;
          border-radius: 9px;
          text-decoration: none;
          color: #1a2b5e !important;
          font-size: 0.875rem;
          font-weight: 500;
          white-space: nowrap;
          transition: background 0.14s ease, padding-left 0.14s ease;
          cursor: pointer;
        }

        .dd-item:hover {
          background: #eef2ff;
          color: #1d40af !important;
          padding-left: 15px;
        }

        .dd-flag {
          font-size: 1.15rem;
          line-height: 1;
          flex-shrink: 0;
        }

        .dd-icon {
          width: 26px;
          height: 26px;
          border-radius: 7px;
          background: #eef2ff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          flex-shrink: 0;
          transition: background 0.14s ease;
        }

        .dd-item:hover .dd-icon { background: #dde6ff; }

        .dd-divider {
          height: 1px;
          background: rgba(0,0,0,0.06);
          margin: 3px 6px;
        }

        /* Default (top - blue nav) */
.btn-contact {
  background: transparent;
  color: white;
  border: 1px solid white;
  transition: all 0.3s ease;
}

/* Scrolled → make it solid blue */
.nav-wrapper.is-scrolled .btn-contact {
  background: #014293 !important;
  color: #ffffff !important;
  border: none !important;
}
/* Default (top - blue navbar) */
.nav-wrapper .nav-list a::after,
.nav-wrapper .nav-dropdown-trigger::after {
  background: white;
}

/* Scrolled (white pill navbar) */
.nav-wrapper.is-scrolled .nav-list a::after,
.nav-wrapper.is-scrolled .nav-dropdown-trigger::after {
  background: #014293;
}
  
/* Optional nice hover */
.nav-wrapper.is-scrolled .btn-contact:hover {
  background: #022f6b !important;
}

        /* ── Mobile dropdown accordion ─────────────────────────── */
        .mobile-dropdown-trigger {
  padding: 15px 1.5em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
  box-sizing: border-box;
}
.mobile-dropdown-trigger span:first-child {
  color: white;
  font-size: 16px;
  font-weight: 500;
}

        .mobile-dd-chevron {
  color: rgba(255, 255, 255, 0.6);
  font-size: 10px;
  transition: transform 0.25s ease;
}

        .mobile-dropdown-trigger.open .mobile-dd-chevron {
  transform: rotate(180deg);
  opacity: 0.8;
}

        .nav-wrapper.is-scrolled .hamburger-line {
  background: #014293;
}
 

        .mobile-dropdown-items {
          overflow: hidden;
          max-height: 0;
          transition: max-height 0.32s ease;
        }

        .mobile-dropdown-items.open { max-height: 220px; }

        .mobile-dd-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 16px 11px 28px;
          text-decoration: none;
          color: white;
          opacity: 0.72;
          font-size: 0.92rem;
          transition: opacity 0.15s ease;
        }

        .mobile-dd-item:hover { opacity: 1; }

        /* ── Wide screen: cap pill width and center it ─────────── */
@media (min-width: 1200px) {
  .nav-wrapper.is-scrolled .inner-nav {
    max-width: 1320px !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }
}

      `}</style>

      <div className={`nav-wrapper ${isScrolled ? "is-scrolled" : ""}`}>
        {/* Holds the exact height when nav goes fixed */}
        <div className="nav-placeholder" style={{ height: navHeight }} />

        <nav className="main-nav" ref={navRef}>
          {/* ↓ .inner-nav is left 100% to your existing CSS when at top.
              Only .is-scrolled overrides kick in when scrolled. */}
          <div className="inner-nav">
            <button
              className={`menu-btn ${isMobileMenuOpen ? "active" : ""}`}
              onClick={toggleMobileMenu}
            >
              <span className="hamburger-line" />
              <span className="hamburger-line" />
              <span className="hamburger-line" />
            </button>

            <div className="l-part">
              <a
                href="#home"
                className="logo"
                onClick={(e) => handleNavClick(e, { id: "home" })}
              >
                <img
                  src={
                    isScrolled
                      ? `${config.imageBaseURL}${config.paths.img}colorlogo.png`
                      : `${config.imageBaseURL}${config.paths.img}logo.png`
                  }
                  alt="CFO Bridge UAE"
                />
              </a>
            </div>

            <div className="m-part">
              <ul className="nav-list">
                {navItems.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.route || `#${item.id}`}
                      onClick={(e) => handleNavClick(e, item)}
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
                {/* Resources */}
                <li>
                  <div
                    className={`nav-dropdown-wrapper ${activeDropdown === "resources" ? "open" : ""}`}
                    onMouseEnter={() => handleMouseEnter("resources")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span
                      className="nav-dropdown-trigger"
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === "resources" ? null : "resources",
                        )
                      }
                    >
                      Resources
                      <svg
                        className="dd-chevron"
                        viewBox="0 0 10 6"
                        fill="none"
                      >
                        <path
                          d="M1 1L5 5L9 1"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <div
                      className="dd-panel"
                      onMouseEnter={() => handleMouseEnter("resources")}
                      onMouseLeave={handleMouseLeave}
                    >
                      {resourceItems.map((res, i) => (
                        <React.Fragment key={res.name}>
                         <a 
                            href={res.route}
                            className="dd-item"
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveDropdown(null);
                              navigate(res.route);
                            }}
                          >
                            {res.name}
                          </a>
                          {i < resourceItems.length - 1 && (
                            <div className="dd-divider" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </li>

                {/* Bridge Ecosystem */}
                <li>
                  <div
                    className={`nav-dropdown-wrapper ${activeDropdown === "ecosystem" ? "open" : ""}`}
                    onMouseEnter={() => handleMouseEnter("ecosystem")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span
                      className="nav-dropdown-trigger"
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === "ecosystem" ? null : "ecosystem",
                        )
                      }
                    >
                      Bridge Ecosystem
                      <svg
                        className="dd-chevron"
                        viewBox="0 0 10 6"
                        fill="none"
                      >
                        <path
                          d="M1 1L5 5L9 1"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <div
                      className="dd-panel"
                      onMouseEnter={() => handleMouseEnter("ecosystem")}
                      onMouseLeave={handleMouseLeave}
                    >
                      {ecosystemItems.map((eco, i) => (
                        <React.Fragment key={eco.name}>
                          <a
                            href={eco.route}
                            className="dd-item"
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveDropdown(null);
                              window.open(eco.route, "_blank"); // opens in new tab
                            }}
                          >
                            {eco.name}
                          </a>
                          {i < ecosystemItems.length - 1 && (
                            <div className="dd-divider" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </li>

                {/* Locations */}
                <li>
                  <div
                    className={`nav-dropdown-wrapper ${activeDropdown === "locations" ? "open" : ""}`}
                    onMouseEnter={() => handleMouseEnter("locations")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span
                      className="nav-dropdown-trigger"
                      onClick={() =>
                        setActiveDropdown(
                          activeDropdown === "locations" ? null : "locations",
                        )
                      }
                    >
                      Locations
                      <svg
                        className="dd-chevron"
                        viewBox="0 0 10 6"
                        fill="none"
                      >
                        <path
                          d="M1 1L5 5L9 1"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <div
                      className="dd-panel"
                      onMouseEnter={() => handleMouseEnter("locations")}
                      onMouseLeave={handleMouseLeave}
                    >
                      {locationItems.map((loc, i) => (
                        <React.Fragment key={loc.name}>
                          <a
                            href={loc.route}
                            className="dd-item"
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveDropdown(null);
                              window.open(loc.route, "_blank");
                            }}
                          >
                            <span
                              className={`fi fi-${loc.flagCode} dd-flag`}
                            ></span>
                            {loc.name}
                          </a>
                          {i < locationItems.length - 1 && (
                            <div className="dd-divider" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="r-part">
              <div className="navbar-actions">
                <a
                  href="#contact"
                  className="btn-contact"
                  onClick={(e) => handleNavClick(e, { id: "contact" })}
                >
                  <span className="btn-text">Get In Touch</span>
                </a>
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile Nav — unchanged */}
      <div className={`mobile-nav ${isMobileMenuOpen ? "active" : ""}`}>
        <div className="menu-head">
          <a
            href="#home"
            className="logo"
            onClick={(e) => handleNavClick(e, { id: "home" })}
          >
            <img
              src={`${config.imageBaseURL}${config.paths.img}logo.webp`}
              alt="CFO Bridge UAE"
            />
          </a>
          <button className="menu-close" onClick={toggleMobileMenu}>
            <span className="close-line" />
            <span className="close-line" />
          </button>
        </div>

        <ul className="nav-list">
          {navItems.map((item) => (
            <li key={item.name}>
              <a
                href={item.route || `#${item.id}`}
                onClick={(e) => handleNavClick(e, item)}
              >
                <span>{item.name}</span>
                <span className="nav-arrow">→</span>
              </a>
            </li>
          ))}
          {/* Resources */}
          <li>
            <div
              className={`mobile-dropdown-trigger ${activeDropdown === "mob-res" ? "open" : ""}`}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "mob-res" ? null : "mob-res",
                )
              }
            >
              <span>Resources</span>
              <span className="mobile-dd-chevron">▼</span>
            </div>
            <div
              className={`mobile-dropdown-items ${activeDropdown === "mob-res" ? "open" : ""}`}
            >
              {resourceItems.map((res) => (
                <a
                  key={res.name}
                  href={res.route}
                  className="mobile-dd-item"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    setActiveDropdown(null);
                    navigate(res.route);
                  }}
                >
                  {res.name}
                </a>
              ))}
            </div>
          </li>

          {/* Bridge Ecosystem */}
          <li>
            <div
              className={`mobile-dropdown-trigger ${activeDropdown === "mob-eco" ? "open" : ""}`}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "mob-eco" ? null : "mob-eco",
                )
              }
            >
              <span>Bridge Ecosystem</span>
              <span className="mobile-dd-chevron">▼</span>
            </div>
            <div
              className={`mobile-dropdown-items ${activeDropdown === "mob-eco" ? "open" : ""}`}
            >
              {ecosystemItems.map((eco) => (
                <a
                  key={eco.name}
                  href={eco.route}
                  className="mobile-dd-item"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setActiveDropdown(null);
                    window.open(eco.route, "_blank");
                  }}
                >
                  {eco.name}
                </a>
              ))}
            </div>
          </li>

          {/* Locations */}
          <li>
            <div
              className={`mobile-dropdown-trigger ${activeDropdown === "mob-loc" ? "open" : ""}`}
              onClick={() =>
                setActiveDropdown(
                  activeDropdown === "mob-loc" ? null : "mob-loc",
                )
              }
            >
              <span>Locations</span>
              <span className="mobile-dd-chevron">▼</span>
            </div>
            <div
              className={`mobile-dropdown-items ${activeDropdown === "mob-loc" ? "open" : ""}`}
            >
              {locationItems.map((loc) => (
                <a
                  key={loc.name}
                  href={loc.route}
                  className="mobile-dd-item"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setActiveDropdown(null);
                    window.location.href = loc.route;
                  }}
                >
                  <span
                    className={`fi fi-${loc.flagCode}`}
                    style={{ marginRight: "8px" }}
                  ></span>
                  {loc.name}
                </a>
              ))}
            </div>
          </li>

          <li className="mobile-contact">
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, { id: "contact" })}
            >
              <span>Contact Us</span>
              <span className="nav-arrow">→</span>
            </a>
          </li>
        </ul>
      </div>
    </>
  );
};

export default Navbar;
